import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMyMatchHistory, useMySquads, useSquadMatchHistory } from '@tark/shared/hooks';
import type { MatchHistoryItem } from '@tark/shared/types';
import {
  BarChart3,
  ChevronDown,
  History,
  List,
  Loader2,
  RefreshCw,
  Trophy,
  Users,
} from 'lucide-react';
import { PersonalSummary } from '../features/matches/PersonalSummary';
import { AppPageFrame } from '../components/AppPageFrame';
import { MatchLedger } from '../features/matches/MatchLedger';

type View = 'games' | 'summary' | 'squad';

const views: Array<{ id: View; label: string; icon: typeof List }> = [
  { id: 'games', label: 'My Games', icon: List },
  { id: 'summary', label: 'Summary', icon: BarChart3 },
  { id: 'squad', label: 'Squad Results', icon: Users },
];

export default function PlayPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const view: View = requestedView === 'squad' || requestedView === 'summary' ? requestedView : 'games';
  const setView = (next: View) => setSearchParams(previous => {
    const params = new URLSearchParams(previous); params.set('view', next); return params;
  });
  const { data: squads = [], isLoading: squadsLoading } = useMySquads();
  const requestedSquadId = Number(searchParams.get('squadId'));
  const squadId = searchParams.has('squadId') ? squads.find(item => item.id === requestedSquadId)?.id ?? null : squads[0]?.id ?? null;
  const setSquadId = (next: number) => setSearchParams(previous => {
    const params = new URLSearchParams(previous); params.set('squadId', String(next)); return params;
  });
  const mine = useMyMatchHistory(view === 'games');
  const squad = useSquadMatchHistory(squadId, view === 'squad');

  useEffect(() => {
    const saved = sessionStorage.getItem('tark:matches:scroll');
    if (!saved) return;
    sessionStorage.removeItem('tark:matches:scroll');
    requestAnimationFrame(() => window.scrollTo({ top: Number(saved), behavior: 'instant' }));
  }, []);

  const query = view === 'squad' ? squad : mine;
  const invalidSquad = view === 'squad' && searchParams.has('squadId') && !squadsLoading && squadId === null;
  const accessDenied = [401,403,404,409].includes((query.error as {response?:{status?:number}} | null)?.response?.status ?? 0);
  const matches = useMemo(
    () => accessDenied ? [] : (query.data?.pages.flatMap((page) => page.matches) ?? [])
      .filter(match => view !== 'squad' || match.squadId === squadId),
    [query.data,accessDenied,view,squadId]
  );

  const openMatch = (match: MatchHistoryItem, ordinal?: number) => {
    sessionStorage.setItem('tark:matches:scroll', String(window.scrollY));
    navigate(`/play/matches/${encodeURIComponent(match.matchId)}${ordinal ? `?scorecard=${ordinal}` : ''}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  return (
    <AppPageFrame className="space-y-6 md:pb-10">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle-readable">Your play</p>
        <h1 className="mt-2 text-3xl font-bold text-text">Matches</h1>
      </header>

      {view === 'squad' ? <p className="text-sm text-text-subtle-readable">
        Saved squad results, including imported matches, remain here after chat cards expire.
      </p> : null}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,5rem),1fr))] gap-1 border-b border-border" role="tablist" aria-label="Match views">
        {views.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={view === id}
            onClick={() => setView(id)}
            className={`flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 border-b-2 px-1 py-2 text-center text-xs font-semibold transition-colors sm:min-h-12 sm:flex-row sm:gap-2 sm:px-2 sm:text-sm ${
              view === id ? 'border-primary text-primary' : 'border-transparent text-text-subtle-readable hover:text-text'
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="w-full min-w-0 whitespace-normal [overflow-wrap:anywhere] sm:w-auto">{label}</span>
          </button>
        ))}
      </div>

      {view === 'summary' ? (
        <PersonalSummary />
      ) : (
        <section aria-live="polite">
          {view === 'squad' ? (
            <label className="mb-5 block max-w-sm">
              <span className="mb-2 block text-sm font-medium text-text">Squad</span>
              <span className="relative block">
                <select
                  aria-label="Choose a squad"
                  value={squadId ?? ''}
                  onChange={(event) => setSquadId(Number(event.target.value))}
                  className="min-h-11 w-full appearance-none rounded-md border border-border bg-surface px-3 py-2 pr-10 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  {squadId === null ? <option value="">Choose a squad</option> : null}
                  {squads.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-3 h-5 w-5 text-text-subtle-readable" />
              </span>
            </label>
          ) : null}

          {query.isLoading ? <MatchListSkeleton /> : null}
          {query.isError ? <HistoryError onRetry={() => void query.refetch()} squad={view === 'squad'} /> : null}
          {invalidSquad ? <p role="alert" className="text-text">This squad is unavailable. Choose a squad you belong to.</p> : null}
          {!invalidSquad && !query.isLoading && !query.isError && matches.length === 0 ? (
            <HistoryEmpty squad={view === 'squad'} hasSquads={squads.length > 0} />
          ) : null}
          {matches.length > 0 ? <MatchLedger matches={matches} showSquad={view !== 'squad'} onOpen={openMatch} /> : null}

          {query.hasNextPage ? (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-border px-4 text-sm font-semibold text-text hover:bg-surface disabled:opacity-60"
              >
                {query.isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                Load older games
              </button>
            </div>
          ) : null}
        </section>
      )}
    </AppPageFrame>
  );
}

function MatchListSkeleton() {
  return <div className="divide-y divide-border border-y border-border" aria-label="Loading matches">
    {[0, 1, 2].map((item) => <div key={item} className="flex animate-pulse items-center gap-3 py-3">
      <div className="h-11 w-11 rounded-full bg-border" />
      <div className="flex-1 space-y-2"><div className="h-4 w-2/3 rounded bg-border" /><div className="h-3 w-1/2 rounded bg-border" /></div>
    </div>)}
  </div>;
}

function HistoryError({ onRetry, squad }: { onRetry: () => void; squad: boolean }) {
  return <div className="border-y border-border py-10 text-center">
    <p className="font-semibold text-text">{squad ? 'Squad results are unavailable' : 'Your games could not be loaded'}</p>
    <p className="mt-1 text-sm text-text-subtle-readable">Check your connection and try again.</p>
    <button type="button" onClick={onRetry} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">
      <RefreshCw className="h-4 w-4" /> Try again
    </button>
  </div>;
}

function HistoryEmpty({ squad, hasSquads }: { squad: boolean; hasSquads: boolean }) {
  const title = squad && !hasSquads ? 'Join a squad to browse results' : squad ? 'No squad results yet' : 'Your games will appear here';
  const body = squad && !hasSquads
    ? 'Once you join a squad, its shared match history will be available here.'
    : squad ? 'Recorded and imported results will appear after the squad has games.'
      : 'Matches you play in TARK and verified imported results will share this timeline.';
  return <div className="border-y border-border py-12 text-center">
    <Trophy className="mx-auto h-6 w-6 text-text-subtle-readable" />
    <p className="mt-3 font-semibold text-text">{title}</p>
    <p className="mx-auto mt-1 max-w-md text-sm text-text-subtle-readable">{body}</p>
  </div>;
}
